<template>
    <div class="animated fadeIn">
        <b-card no-body>
            <b-card-header>
                <i class="fa fa-folder-open mr-2"></i>
                <strong>Gestionnaire de Fichiers SFTP</strong>
            </b-card-header>
            
            <b-tabs card v-model="activeTab">
                
                <b-tab title="Upload des fichiers" active>
                    <b-row class="justify-content-center my-3">
                        <b-col md="8">
                            <b-form-group label="1. Choisir le dossier de destination :" label-for="upload-destination">
                                <b-form-select 
                                    id="upload-destination" 
                                    v-model="uploadDest" 
                                    :options="destinationsOptions"
                                >
                                    <template #first>
                                        <b-form-select-option :value="null" disabled>-- Veuillez sélectionner un dossier cible --</b-form-select-option>
                                    </template>
                                </b-form-select>
                            </b-form-group>
                            
                            <b-form-group 
                                label="2. Sous-dossier spécifique (Optionnel) :" 
                                label-for="upload-subfolder"
                                description="Exemple: 'a_traiter' ou 'archives/2026' (sans slashs au début ni à la fin)"
                            >
                                <b-form-input
                                    id="upload-subfolder"
                                    v-model="uploadSubfolder"
                                    type="text"
                                    placeholder="Ex: a_traiter"
                                    :disabled="!uploadDest"
                                    @input="sanitizeSubfolder('uploadSubfolder')"
                                ></b-form-input>
                                <small v-if="uploadDest" class="text-info d-block mt-1">
                                    <i class="fa fa-eye mr-1"></i> <strong>Chemin final :</strong> {{ computedUploadPath }}
                                </small>
                            </b-form-group>
                            
                            <b-form-group label="3. Déposer le fichier :" label-for="file-input" class="mt-4">
                                <b-form-file
                                    id="file-input"
                                    v-model="selectedFile"
                                    :disabled="!uploadDest"
                                    placeholder="Glissez-déposez un fichier ici ou cliquez pour parcourir..."
                                    drop-placeholder="Déposez le fichier ici..."
                                    browse-text="Parcourir"
                                ></b-form-file>
                            </b-form-group>
                            
                            <div class="text-right mt-4">
                                <b-button 
                                    type="button" 
                                    variant="primary" 
                                    :disabled="!uploadDest || !selectedFile" 
                                    @click="uploadFile"
                                >
                                    <i class="fa fa-upload mr-1"></i> Envoyer sur le serveur
                                </b-button>
                            </div>
                        </b-col>
                    </b-row>
                </b-tab>
                
                <b-tab title="Déplacer un fichier">
                    <b-row class="justify-content-center my-3">
                        <b-col md="10">
                            
                            <b-row>
                                <b-col md="6" class="border-right">
                                    <h5 class="text-secondary mb-3"><i class="fa fa-reply mr-2"></i>Source (Origine)</h5>
                                    
                                    <b-form-group label="Dossier Source :" label-for="source-dir">
                                        <b-form-select id="source-dir" v-model="sourceDest" :options="destinationsOptions">
                                            <template #first>
                                                <b-form-select-option :value="null" disabled>-- Sélectionner le dossier source --</b-form-select-option>
                                            </template>
                                        </b-form-select>
                                    </b-form-group>

                                    <b-form-group label="Sous-dossier source (Optionnel) :" label-for="source-subfolder">
                                        <b-form-input
                                            id="source-subfolder"
                                            v-model="sourceSubfolder"
                                            type="text"
                                            placeholder="Ex: archives/2026"
                                            :disabled="!sourceDest"
                                            @input="sanitizeSubfolder('sourceSubfolder')"
                                        ></b-form-input>
                                    </b-form-group>
                                    
                                    <b-form-group label="Nom du fichier exact avec son extension :" label-for="source-filename">
                                        <b-form-input
                                            id="source-filename"
                                            v-model="sourceFilename"
                                            type="text"
                                            placeholder="Ex: import_beneficiaires.csv"
                                            :disabled="!sourceDest"
                                        ></b-form-input>
                                    </b-form-group>
                                </b-col>
                                
                                <b-col md="6">
                                    <h5 class="text-success mb-3"><i class="fa fa-share mr-2"></i>Destination (Cible)</h5>
                                    
                                    <b-form-group label="Dossier Cible :" label-for="target-dir">
                                        <b-form-select id="target-dir" v-model="targetDest" :options="destinationsOptions">
                                            <template #first>
                                                <b-form-select-option :value="null" disabled>-- Sélectionner le dossier cible --</b-form-select-option>
                                            </template>
                                        </b-form-select>
                                    </b-form-group>
                                    
                                    <b-form-group label="Sous-dossier spécifique (Optionnel) :" label-for="target-subfolder">
                                        <b-form-input
                                            id="target-subfolder"
                                            v-model="targetSubfolder"
                                            type="text"
                                            placeholder="Ex: a_traiter"
                                            :disabled="!targetDest"
                                            @input="sanitizeSubfolder('targetSubfolder')"
                                        ></b-form-input>
                                    </b-form-group>

                                    <b-form-group 
                                        label="Nouveau nom du fichier avec son extension (Optionnel) :" 
                                        label-for="rename-filename"
                                        description="Laissez vide pour conserver le nom d'origine"
                                    >
                                        <b-form-input
                                            id="rename-filename"
                                            v-model="renameFilename"
                                            type="text"
                                            placeholder="Ex: nouveau_nom.csv"
                                            :disabled="!targetDest || !sourceFilename"
                                        ></b-form-input>
                                    </b-form-group>
                                </b-col>
                            </b-row>
                            
                            <b-row class="mt-4" v-if="sourceDest && targetDest && sourceFilename">
                                <b-col md="12">
                                    <div class="alert alert-info">
                                        <div><strong><i class="fa fa-arrow-right mr-1"></i> Déplacement prévu :</strong></div>
                                        <small class="d-block mt-1"><strong>Depuis :</strong> {{ computedFullSourcePath }}</small>
                                        <small class="d-block">
                                            <strong>Vers :</strong> {{ computedFullTargetPath }}
                                            <b-badge v-if="renameFilename" variant="warning" class="ml-2">Renommage actif</b-badge>
                                        </small>
                                    </div>
                                </b-col>
                            </b-row>
                            
                            <div class="text-right mt-3">
                                <b-button 
                                    type="button" 
                                    variant="success" 
                                    :disabled="!sourceDest || !targetDest || !sourceFilename || (computedFullSourcePath === computedFullTargetPath)"
                                    @click="moveFiles"
                                >
                                    <i class="fa fa-exchange mr-1"></i> Exécuter le déplacement
                                </b-button>
                            </div>
                            
                        </b-col>
                    </b-row>
                </b-tab>
            </b-tabs>
        </b-card>
    </div>
</template>

<script>
import { rules, hasRule } from '@/utils/rulesHelper';
import { isEmpty } from "@/utils/fileHelper";

export default {
    name: 'GestionFichiersIndex',
    data() {
        return {
            rules: rules,
            activeTab: 0,
            
            // Données d'Upload (Onglet 1)
            uploadDest: null,
            uploadSubfolder: '',
            selectedFile: null,
            
            // Données de Déplacement (Onglet 2)
            sourceDest: null,
            sourceSubfolder: '',
            targetDest: null,
            targetSubfolder: '',
            sourceFilename: '',
            renameFilename: '',
            
            // Dictionnaire des paths absolus sur serveurs
            destinationsMap: {
                'docroot': '/private/',
                'docroot_espace_bene': '/private/espace_beneficiaire/',
                'docroot_espace_bene_rib': '/private/espace_beneficiaire/rib/',
                'docroot_prestij': '/private/prestij/',
                'docroot_garantie': '/private/garantie/',
                'docroot_gestion_pj': '/idm/gestion/dossier/pieces_jointes/',
                'docroot_fichier_integration_nsi': '/idm/gestion/module_prev/envoi/',
                'docroot_fichier_retour_integration_nsi': '/idm/gestion/module_prev/ar/',
                'docroot_fichier_rejet_integration_nsi': '/idm/gestion/module_prev/rejet/',
                'docroot_fichier_prestij': '/idm/gestion/prestij/',
                'docroot_opc': '/idm/gestion/opc/',
                'docroot_presence_verte': '/idm/gestion/listing/presence_verte/',
                'docroot_agira': '/idm/gestion/listing/agira/',
                'docroot_actualite_pj': '/private/gestion/actualite/',
                'docroot_consigne_pj': '/private/gestion/consigne/',
                'docroot_flux_rentes': '/private/flux/rentes/',
                'docroot_flux_decomptes_a_traiter': '/private/flux/decompte/a_traiter/',
                'docroot_reprise_chorum': '/private/gestion/reprise/',
                'docroot_bibliotheque_fichiers': '/private/bibliotheque/document/fichiers/',
                'docroot_fichier_eden': '/idm/gestion/module_eden/envoi/',
                'docroot_flux_fcp': '/private/flux/fcp/',
                'docroot_flux_affiliation': '/private/flux/affiliation/'
            },
            
            // Options du Select groupées
            destinationsOptions: [
                { label: 'Général', options: [
                    { value: 'docroot', text: 'Racine (/private/)' }
                ]},
                { label: 'Métier & Bénéficiaires', options: [
                    { value: 'docroot_espace_bene', text: 'Espace Bénéficiaire - Décomptes Inval DC + justificatifs - (/private/espace_beneficiaire/)' },
                    { value: 'docroot_garantie', text: 'Garantie - (/private/garantie/)' }, 
                    { value: 'docroot_flux_rentes', text: 'RQ 01 - Flux Rentes - (/private/flux/rentes/)' },
                    { value: 'docroot_flux_decomptes_a_traiter', text: 'Flux Décomptes IJ - (/private/flux/decompte/a_traiter/)' }
                ]},
                { label: 'Gestion Interne', options: [
                    { value: 'docroot_gestion_pj', text: 'Gestion Pièces Jointes - (/idm/gestion/dossier/pieces_jointes/)' },
                    { value: 'docroot_actualite_pj', text: 'Actualités Pièces Jointes - (/private/gestion/actualite/)' },
                    { value: 'docroot_consigne_pj', text: 'Consignes Pièces Jointes - (/private/gestion/consigne/)' },
                    { value: 'docroot_bibliotheque_fichiers', text: 'Bibliothèque Documentaire - (/private/bibliotheque/document/fichiers/)' },
                    { value: 'docroot_reprise_chorum', text: 'Reprise Chorum - (/private/gestion/reprise/)' }
                ]},
                { label: 'Prestij & Partenaires', options: [
                    { value: 'docroot_prestij', text: 'Prestij Privé - (/private/prestij/)' },
                    { value: 'docroot_fichier_prestij', text: 'Prestij Gestion - (/idm/gestion/prestij/)' },
                    { value: 'docroot_opc', text: 'OPC - (/idm/gestion/opc/)' },
                    { value: 'docroot_presence_verte', text: 'Listing (IJ) Présence Verte - (/idm/gestion/listing/presence_verte/)' }, 
                    { value: 'docroot_agira', text: 'Agira (Listing Décès) - (/idm/gestion/listing/agira/)' }
                ]},
                { label: 'Intégrations NSI & Eden', options: [
                    { value: 'docroot_fichier_integration_nsi', text: 'Module Prev - Envoi Intégration NSI- (/idm/gestion/module_prev/envoi/)' },
                    { value: 'docroot_fichier_retour_integration_nsi', text: 'Module Prev - Accusé Réception NSI - (/idm/gestion/module_prev/ar/)' },
                    { value: 'docroot_fichier_rejet_integration_nsi', text: 'Module Prev - Rejets NSI - /idm/gestion/module_prev/rejet/' },
                    { value: 'docroot_fichier_eden', text: 'Eden - Envoi - (/idm/gestion/module_eden/envoi/)' }
                ]},
                 { label: 'Référentiels', options: [
                    { value: 'docroot_flux_fcp', text: 'Flux FCP (/private/flux/fcp/)' },
                    { value: 'docroot_flux_affiliation', text: 'Flux Affliiation - (/private/flux/affiliation/)' }
                ]}
            ]
        };
    },
    computed: {
        /**
         * Calcule le dossier de dépôt final (Onglet Upload)
         */
        computedUploadPath() {
            if (!this.uploadDest) return '';
            let basePath = this.destinationsMap[this.uploadDest];
            if (this.uploadSubfolder) {
                return `${basePath}${this.uploadSubfolder}/`;
            }
            return basePath;
        },
        
        /**
         * Calcule le chemin absolu du fichier source
         */
        computedFullSourcePath() {
            if (!this.sourceDest || !this.sourceFilename) return '';
            let basePath = this.destinationsMap[this.sourceDest];
            if (this.sourceSubfolder) {
                return `${basePath}${this.sourceSubfolder}/${this.sourceFilename}`;
            }
            return `${basePath}${this.sourceFilename}`;
        },
        
        /**
         * Calcule le chemin absolu attendu en destination
         */
        computedFullTargetPath() {
            if (!this.targetDest || !this.sourceFilename) return '';
            let basePath = this.destinationsMap[this.targetDest];
            
            let filenameToUse = this.renameFilename.trim() !== '' ? this.renameFilename.trim() : this.sourceFilename;
            
            if (this.targetSubfolder) {
                return `${basePath}${this.targetSubfolder}/${filenameToUse}`;
            }
            return `${basePath}${filenameToUse}`;
        }
    },
    methods: {
        canAccess(rule) {
            return hasRule(rule);
        },
        
        /**
         * Nettoie les anti-slashs et slashs au début pour éviter les chemins cassés
         */
        sanitizeSubfolder(variableName) {
            let value = this[variableName].replace(/\\/g, '/');
            if (value.startsWith('/')) value = value.substring(1);
            this[variableName] = value;
        },
        
        /**
         * Envoi du fichier à l'API (Upload)
         */
        uploadFile() {
            if (!this.selectedFile || !this.uploadDest) return;
            
            let self = this;
            this.$store.commit("IS_LOADING", true);
            
            let formData = new FormData();
            formData.append("file", this.selectedFile);
            formData.append("full_path", this.computedUploadPath);
            
            this.$axios
                .post("/administration/gestion-fichiers/upload", formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                })
                .then(() => {
                    this.$store.commit("IS_LOADING", false);
                    this.selectedFile = null;
                    this.uploadSubfolder = '';
                    
                    this.$swal({
                        position: "top",
                        type: "success",
                        title: "Upload réussi",
                        text: `Le fichier a été envoyé dans : ${this.computedUploadPath}`,
                        showConfirmButton: true
                    });
                })
                .catch((error) => {
                    self.$store.commit("IS_LOADING", false);
                    self.handleError(error);
                });
        },
        
        /**
         * Envoi des instructions de déplacement à l'API (+ Option Renommage)
         */
        moveFiles() {
            if (!this.sourceDest || !this.targetDest || !this.sourceFilename) return;
            
            this.$store.commit("IS_LOADING", true);
            
            this.$axios
                .post("/administration/gestion-fichiers/move", {
                    source_full_path: this.computedFullSourcePath,
                    target_full_path: this.computedFullTargetPath,
                    rename_file: this.renameFilename.trim() !== '' ? this.renameFilename.trim() : null
                })
                .then(() => {
                    this.$store.commit("IS_LOADING", false);
                    this.sourceFilename = ''; 
                    this.renameFilename = '';
                    this.sourceSubfolder = '';
                    this.targetSubfolder = '';
                    this.$swal({
                        position: "top",
                        type: "success",
                        title: "Déplacement réussi",
                        text: "Le fichier a été traité avec succès sur le serveur SFTP.",
                        timer: 3000
                    });
                })
                .catch((error) => {
                    this.$store.commit("IS_LOADING", false);
                    this.handleError(error);
                });
        },
        
        handleError(error) {
            console.error(error);
            let text = 'Une erreur est survenue, si le problème persiste merci de faire un ticket au support DOSI';
            if (!isEmpty(error) && error.response) {
                if (error.response.status === 403) {
                    text = "Vous n'avez pas les droits nécessaires pour effectuer cette action.";
                } else if (error.response.status === 406 || error.response.status === 424 || error.response.status === 415) {
                    text = error.response.data.message;
                }
            }
            this.$swal({ position: 'top', type: 'error', title: 'Attention !!', text: text, showConfirmButton: false, timer: 5000 });
        }
    }
};
</script>

<style scoped>
.clickable {
    cursor: pointer;
}
</style>